/* eslint-disable @typescript-eslint/no-explicit-any */
import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { mediumRepository } from "../repositories/mediumRepository";
import type { QuickMediumSignupPayload } from "../types";
import {
  normalizeMediumSignupError,
  type MediumSignupError,
} from "./useMediumSignupMutation";
import { sessionQueryKey } from "./useSessionQuery";

export type QuickMediumSignupResponse = {
  message?: string;
  profileComplete: boolean;
  user?: {
    profile?: Record<string, unknown>;
    terreiro?: {
      id?: string;
      [k: string]: unknown;
    };
    [k: string]: unknown;
  };
  // medium is at root level in the response (not inside user)
  medium?: {
    id?: string;
    [k: string]: unknown;
  };
  terreiro?: {
    id?: string;
    [k: string]: unknown;
  } | null;
  [k: string]: unknown;
};

/**
 * React Query mutation hook for quick medium signup.
 * Uses the /medium-signup/quick endpoint which requires only essential fields.
 */
export const useQuickMediumSignupMutation = (
  options?: UseMutationOptions<
    QuickMediumSignupResponse,
    MediumSignupError,
    QuickMediumSignupPayload
  >,
) => {
  const queryClient = useQueryClient();

  const wrappedOnSuccess: UseMutationOptions<
    QuickMediumSignupResponse,
    MediumSignupError,
    QuickMediumSignupPayload
  >["onSuccess"] = async (data, variables, context) => {
    try {
      if (data?.user) {
        // Update session cache so AuthContext/ProtectedRoute sees auth immediately
        queryClient.setQueryData(sessionQueryKey, data.user);

        // Also update legacy caches for profile/terreiro if needed
        const profile = data.user.profile;
        const terreiro = data.terreiro;
        const terreiroId = terreiro?.id;
        // Note: medium is at root level in the API response, not inside user
        const medium = data.medium;
        const mediumId = medium?.id;

        if (profile) {
          queryClient.setQueryData(["auth", "currentUser"], profile);
          try {
            localStorage.setItem(
              "ilovemacumba:auth:currentUser",
              JSON.stringify(profile),
            );
          } catch {
            // ignore localStorage errors
          }
        }

        if (terreiroId) {
          queryClient.setQueryData(["terreiro", terreiroId], terreiro);
          queryClient.setQueryData(["terreiro", "current"], terreiro);
          try {
            localStorage.setItem(
              "ilovemacumba:terreiro:current",
              JSON.stringify(terreiro),
            );
          } catch {
            // ignore localStorage errors
          }
        }

        // Save medium data to local storage when onboarding a new medium
        if (mediumId && medium) {
          queryClient.setQueryData(["medium", mediumId], medium);
          queryClient.setQueryData(["medium", "current"], medium);
          try {
            localStorage.setItem(
              "ilovemacumba:medium:current",
              JSON.stringify(medium),
            );
          } catch {
            // ignore localStorage errors
          }
        }

        // Store profile completion status
        if (typeof data.profileComplete === "boolean") {
          try {
            localStorage.setItem(
              "ilovemacumba:profile:complete",
              JSON.stringify(data.profileComplete),
            );
          } catch {
            // ignore localStorage errors
          }
        }
      } else {
        // ensure at least non-null value until /auth/me refetch
        queryClient.setQueryData(sessionQueryKey, null);
      }

      // Trigger a background refetch to confirm session via /v1/auth/me
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
    } catch {
      // swallow cache/localStorage errors
    }

    options?.onSuccess?.(data, variables, context as any);
  };

  return useMutation<
    QuickMediumSignupResponse,
    MediumSignupError,
    QuickMediumSignupPayload
  >({
    mutationFn: (data: QuickMediumSignupPayload) =>
      mediumRepository.quickSignup(data).catch((err) => {
        throw normalizeMediumSignupError(err);
      }),
    ...options,
    onSuccess: wrappedOnSuccess,
  });
};

export default useQuickMediumSignupMutation;
